import { CollectionPreferencesProps, Pagination, Table, TableProps } from '@cloudscape-design/components';
import PropertyFilter from '@cloudscape-design/components/property-filter';
import Alert from '@cloudscape-design/components/alert';
import { getTextFilterCounterText } from '../../../../../../utils/text-filter';
import { FILTERING_PROPERTIES as filteringProperties } from './filteringProperties';

import { DEFAULT_PREFERENCES, tableColumns, Preferences } from './table.config';

import { useDashboardManager } from '../../hooks/useDashboardManager';
import { WidgetTablePreferences, WidgetTableProps } from '../interfaces';
import { ProtectedUtilityUpdate } from '../../../../../../components/protectedUtility';
import { ResourceTypes } from '../../../../../../types/rolePermissions';
import { useCatoCollection } from '../../../../../../utils/hooks/useCatoCollection';
import { ContactTraceRecordSnapshot } from './types';
import { NonCancelableCustomEvent } from '@cloudscape-design/components/internal/events';

export default function WidgetTable({ widgetId, isLoading, data = [], error }: Readonly<WidgetTableProps<ContactTraceRecordSnapshot[]>>) {
  const { getWidget, setWidget } = useDashboardManager();
  const widget = getWidget<WidgetTablePreferences>(widgetId);
  const columnDefinitions: TableProps.ColumnDefinition<ContactTraceRecordSnapshot>[] = tableColumns.map((tableColumn) => ({
    ...tableColumn,
    width: (tableColumn.id && widget?.preferences?.widths?.[tableColumn.id]) || tableColumn.width,
  }));
  const preferences = widget?.preferences?.tablePreferences ?? DEFAULT_PREFERENCES;

  const saveWidths = (event: NonCancelableCustomEvent<TableProps.ColumnWidthsChangeDetail>) => {
    const { widths } = event.detail;

    const widgetPreferences = {
      ...widget.preferences,
      widths: columnDefinitions.reduce(
        (response: Record<string, number>, definition, index) => ({ ...response, ...(definition.id && { [definition.id]: widths[index] }) }),
        {},
      ),
    };

    setWidget<WidgetTablePreferences>(widgetId, { preferences: widgetPreferences });
  };

  const setPreferences = (preferences: CollectionPreferencesProps.Preferences) => {
    const updatedPreferences = { ...widget.preferences, tablePreferences: preferences };

    setWidget(widgetId, { preferences: updatedPreferences });
  };

  const { items, filteredItemsCount, collectionProps, propertyFilterProps, paginationProps } = useCatoCollection(data, {
    filteringProperties,
    pageSize: preferences.pageSize,
  });

  return (
    <>
      <Table
        {...collectionProps}
        stickyHeader={true}
        resizableColumns={true}
        onColumnWidthsChange={saveWidths}
        columnDefinitions={columnDefinitions}
        items={items}
        trackBy='currentAgentSnapshotConfigurationUsername'
        loadingText='Loading data'
        columnDisplay={preferences.contentDisplay}
        wrapLines={preferences.wrapLines}
        stripedRows={preferences.stripedRows}
        stickyColumns={preferences.stickyColumns}
        contentDensity={preferences.contentDensity as 'comfortable' | 'compact'}
        variant='embedded'
        loading={isLoading}
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            countText={getTextFilterCounterText(filteredItemsCount)}
            expandToViewport={true}
            filteringAriaLabel={'Filter table'}
            filteringPlaceholder={'Filter table'}
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <ProtectedUtilityUpdate resourceType={ResourceTypes.Dashboards}>
            <Preferences
              preferences={preferences}
              setPreferences={setPreferences}
            />
          </ProtectedUtilityUpdate>
        }
      />
      {error && (
        <Alert
          statusIconAriaLabel='Error'
          type='error'
          header='Error getting data'
        >
          Refresh the page to try again.
        </Alert>
      )}
    </>
  );
}
